import { useCallback } from 'react';

import { uniqBy } from 'lodash-es';

import {
	INSTANT_SEARCH_CACHE_SECONDS,
	INSTANT_SEARCH_LOCATION_FILTER_MAX_ITEMS,
	TYPESENSE_LOCATION_COLLECTION,
	TYPESENSE_LOCATION_FILTER,
} from '#src/app/pages/search/shared/consts';
import type { LocationItem } from '#src/app/pages/search/shared/types';

import { useSearchClient } from '../../../../../useSearchClient';

type LocationHit = {
	id: string;
	city: string;
	state_code: string;
	country_code: string;
};

type Result = ModelHookResult<
	undefined,
	{
		searchForCities: (queryLocation: string, selectedLocation: string) => Promise<readonly LocationItem[]>;
	}
>;

export function useSearchForCities(): Result {
	const {
		data: { typesenseClient },
	} = useSearchClient();

	const getCityOrStateLocation = useCallback((city?: string, stateCode?: string) => {
		if (!city || !stateCode) return `${city || stateCode}`;
		return `${city}, ${stateCode}`;
	}, []);

	return {
		actions: {
			searchForCities: useCallback(
				async (queryLocation: string, selectedLocation: string) => {
					const response = await typesenseClient
						.collections<LocationHit>(TYPESENSE_LOCATION_COLLECTION)
						.documents()
						.search(
							{
								q: queryLocation,
								query_by: TYPESENSE_LOCATION_FILTER,
								limit: INSTANT_SEARCH_LOCATION_FILTER_MAX_ITEMS,
							},
							{
								cacheSearchResultsForSeconds: INSTANT_SEARCH_CACHE_SECONDS,
							},
						);

					if (!response.hits?.length) return [];

					return uniqBy(
						response.hits.map(({ document }) => {
							const { city, state_code: stateCode } = document as LocationHit;

							const cityStateValue = getCityOrStateLocation(city, stateCode);

							return {
								label: cityStateValue,
								value: cityStateValue,
								count: 0,
								isRefined: selectedLocation === cityStateValue,
								type: 'city' as const,
							};
						}),
						'value',
					);
				},
				[getCityOrStateLocation, typesenseClient],
			),
		},
	};
}
